import React, {useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {Check, NavigateBefore, NavigateNext} from '@mui/icons-material';
import {Popover, Skeleton} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    AddButtonLight,
    CheckboxesTags,
    FilterPopover,
    MinLoader,
    NoItemsYet,
    SelectAutocompletePaginationMultiple,
    SelectedFilters,
    StatusRow,
} from 'components';
import {clientActions} from 'store';
import {
    authUtilizationFilterStatusEnums,
    authUtilizationStatuses,
    clientStatuses,
    FindErrorItem,
    FindLoad,
    Images,
    renderClientName,
    SaveParams,
} from 'utils';
import {
    AUTH_UTILIZATION_ACTION,
    NEUTRAL_CHIP_THEME,
    PAGE_SIZE,
    SKELETON_ROWS,
    STATUS_THEME,
    filterStatusDot,
    getTimelineRange,
} from './constants';
import {buildUtilizationClients} from './buildUtilization';
import {UtilizationTimeline} from './utilizationTimeline';
import './authUtilization.scss';

/** `location.state` keys owned by the filter popover — the Status chips live in the toolbar, not the popover. */
const POPOVER_FILTER_KEYS = ['clients', 'clientStatuses'];

/** Swatch per Status option: the pace / Available dots, or the Scheduled / Cancelled-expired bars the timeline uses. */
const STATUS_SWATCH_CLASS = {
    [authUtilizationFilterStatusEnums.SCHEDULED]: 'auth-util-legend-scheduled',
    [authUtilizationFilterStatusEnums.CANCELLED]: 'auth-util-legend-inactive',
};

/**
 * The legend doubles as the Status filter: each chip toggles its option. Selected chips take the status tint and a
 * check, deselected ones go white. `value` is a list of option ids; every option is selected by default.
 */
const StatusChips = ({value, onChange}) => {
    const toggle = (id) =>
        onChange(authUtilizationStatuses.filter((item) => (item.id === id ? !value.includes(id) : value.includes(item.id))).map((item) => item.id));

    return (
        <div className="auth-util-legend" role="group" aria-label="Filter by status">
            {authUtilizationStatuses.map((item) => {
                const selected = value.includes(item.id);
                const theme = STATUS_THEME[item.id] || NEUTRAL_CHIP_THEME;
                const swatchClass = STATUS_SWATCH_CLASS[item.id];
                return (
                    <button
                        key={item.id}
                        type="button"
                        aria-pressed={selected}
                        className={`auth-util-legend-chip${selected ? ' auth-util-legend-chip--selected' : ''}`}
                        style={{'--chip-bg': theme.bg, '--chip-border': theme.border, '--chip-check': theme.dot}}
                        onClick={() => toggle(item.id)}>
                        {swatchClass ? <i className={swatchClass}/> :
                            <i className="auth-util-legend-dot" style={filterStatusDot(item.id)}/>}
                        {item.label}
                        {/*{selected && <Check className="auth-util-legend-check"/>}*/}
                    </button>
                );
            })}
        </div>
    );
};

/** Same paginated client picker as the Schedule and Clients-Staff map filters; `value` holds the picked client objects. */
const ClientFilter = ({value, onChange}) => (
    <SelectAutocompletePaginationMultiple
        type="client"
        name="clients"
        outLabel="Client"
        uiType="tableFilter"
        placeholder={value.length ? value.map((client) => renderClientName(client)).join(', ') : 'All'}
        defaultValue={value}
        renderValue={(client) => renderClientName(client)}
        handleSelect={(event) => onChange(event.target.full || [])}
        noError
    />
);

/** `value` is a list of client status ids; nothing selected = every client. */
const ClientStatusFilter = ({value, onChange}) => {
    const selected = clientStatuses.filter((item) => value.includes(item.id));

    return (
        <CheckboxesTags
            styles={{width: 'auto'}}
            name="clientStatuses"
            value={selected}
            permissionsList={clientStatuses}
            handleChange={(list) => onChange(list.map((item) => item.id))}
            renderValue={(item) => item?.label}
            renderRow={(item) => <StatusRow status={item.id}/>}
            outLabel="Client Status"
            placeholder={selected.length ? selected.map((item) => item.label).join(', ') : 'All'}
            uiType="tableFilter"
            notShowError
        />
    );
};

/**
 * Next to the filter button: ‹ › step the timeline a whole month; the label opens a month picker to jump straight
 * to one. Same look as the Schedule page's week navigation (its date-range dropdown, but picking a month).
 */
const MonthNavigation = ({anchor, onPrev, onNext, onSelect}) => {
    const [pickerAnchor, setPickerAnchor] = useState(null);

    const handleSelect = (date) => {
        setPickerAnchor(null);
        if (date) onSelect(date);
    };

    return (
        <div className="auth-util-month-nav">
            <div className="auth-util-month-nav-arrows">
                <NavigateBefore style={{color: '#387DFF'}} onClick={onPrev} aria-label="Previous month"/>
                <NavigateNext style={{color: '#387DFF'}} onClick={onNext} aria-label="Next month"/>
            </div>
            <button
                type="button"
                className="auth-util-month-nav-label"
                aria-haspopup="dialog"
                aria-expanded={!!pickerAnchor}
                onClick={(event) => setPickerAnchor(event.currentTarget)}>
                <span>{anchor.format('MMMM YYYY')}</span>
                <img src={Images.dropdownArrow} alt="" className={pickerAnchor ? 'auth-util-month-nav-arrow--open' : ''}/>
            </button>
            <Popover
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {className: 'auth-util-month-picker'}}}>
                <DatePicker inline showMonthYearPicker selected={anchor.toDate()} onChange={handleSelect}/>
            </Popover>
        </div>
    );
};

/** Placeholder rows under the timeline header while the first page loads and there are no rows to keep on screen. */
const TimelineSkeleton = () => (
    <div className="auth-util-skeleton" aria-busy="true">
        {Array.from({length: SKELETON_ROWS}, (_, index) => (
            <div key={index} className="auth-util-row auth-util-skeleton-row">
                <div className="auth-util-client-col auth-util-skeleton-client">
                    <Skeleton variant="text" width="70%" height={20}/>
                </div>
                <div className="auth-util-track auth-util-skeleton-track">
                    <Skeleton variant="rounded" height={22} width={`${35 + ((index * 23) % 50)}%`}
                              style={{marginLeft: `${(index * 17) % 30}%`}}/>
                </div>
            </div>
        ))}
    </div>
);

const LoadFailed = ({text, onRetry}) => (
    <div className="auth-util-more auth-util-load-failed" role="alert">
        <span>{text}</span>
        <AddButtonLight addButnLightInnerText="Retry" onAddButnLightClick={onRetry}/>
    </div>
);

/**
 * Pages of `[{ client, authorizations }]` from `GET /authorizedService/utilization`, PAGE_SIZE clients at a time.
 * Changing the range, search, statuses, clients or client statuses starts over from the first page; `loadMore`
 * appends the next one. `statuses` are filter-status values, empty = all (not sent). `enabled` false (no Status
 * option checked) sends nothing — the page shows the empty state instead.
 * A failed request stops auto-loading — the sentinel would otherwise re-fire it on every render — until `retry`
 * or a filter change (each request clears the error).
 *
 * While a replacement page loads, the rows already in the store are the *previous* range's, so they're laid out
 * against `applied` (the range/statuses they were fetched for) instead of the new ones — otherwise the old bars
 * would jump onto the new month's axis before the fresh data lands.
 */
const useUtilizationPages = (range, search, statuses, clientIds, clientStatusIds, enabled) => {
    const dispatch = useDispatch();
    const {clients: entries, count} = useSelector((state) => state.client.authUtilization);
    const loading = !!FindLoad(AUTH_UTILIZATION_ACTION)?.length;
    const failed = !!FindErrorItem(AUTH_UTILIZATION_ACTION);
    // Skip of the latest request: 0 means the list is being replaced.
    const lastSkip = useRef(0);
    const [applied, setApplied] = useState({range, statuses});
    // Filters of the in-flight replacement request; promoted to `applied` once it settles.
    const pending = useRef(null);

    useEffect(() => {
        if (!loading && pending.current) {
            setApplied(pending.current);
            pending.current = null;
        }
    }, [loading]);

    const request = (skip) => {
        lastSkip.current = skip;
        if (!skip) pending.current = {range, statuses};
        dispatch(
            clientActions.getAuthUtilization({
                startDate: range.start.format('YYYY-MM-DD'),
                endDate: range.end.format('YYYY-MM-DD'),
                search: search || undefined,
                statuses: statuses.length ? statuses : undefined,
                clientIds: clientIds.length ? clientIds : undefined,
                clientStatuses: clientStatusIds.length ? clientStatusIds : undefined,
                skip,
                limit: PAGE_SIZE,
            })
        );
    };

    useEffect(() => {
        if (enabled) request(0);
    }, [range, search, statuses, clientIds, clientStatusIds, enabled]);

    const hasMore = entries.length < count;
    const loadMore = () => {
        if (enabled && !loading && hasMore && !failed) request(entries.length);
    };
    const retry = () => enabled && request(entries.length);

    return {entries, applied, loading, reloading: loading && !lastSkip.current, hasMore, failed, loadMore, retry};
};

export const AuthUtilization = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const info = location?.state;
    const search = info?.searchValue?.trim() || '';
    const [anchor, setAnchor] = useState(() => moment().startOf('day'));

    /**
     * Filters live in `location.state`, like every other grid, so the shared SelectedFilters chip bar can chart and
     * remove them: `clients` (picked client objects), `clientStatuses` (ids) and `statuses` (Status option ids).
     * An absent key is the default — no client / client-status narrowing, every Status option checked; `statuses: []`
     * is "none checked". Values are read through joined keys because `location.state` is a new object on every
     * navigate, and the fetch effect must only re-run when a filter actually changes.
     */
    const clientsKey = (info?.clients || []).map((client) => client.id).join(',');
    const clientStatusesKey = (info?.clientStatuses || []).join(',');
    const statusesKey = (info?.statuses ?? authUtilizationStatuses.map((item) => item.id)).join(',');
    const selectedClients = useMemo(() => info?.clients || [], [clientsKey]);
    const clientIds = useMemo(() => (clientsKey ? clientsKey.split(',') : []), [clientsKey]);
    const clientStatusIds = useMemo(() => (clientStatusesKey ? clientStatusesKey.split(',') : []), [clientStatusesKey]);
    const statusIds = useMemo(() => (statusesKey ? statusesKey.split(',') : []), [statusesKey]);

    /** Writes one filter to `location.state`; `undefined` removes the key (back to its default). */
    const saveFilter = (key, value) => {
        const params = {...info};
        if (value === undefined) delete params[key];
        else params[key] = value;
        SaveParams(navigate, params);
    };
    const setSelectedClients = (list) => saveFilter('clients', list.length ? list : undefined);
    const setClientStatusIds = (ids) => saveFilter('clientStatuses', ids.length ? ids : undefined);
    const setStatusIds = (ids) => saveFilter('statuses', ids.length === authUtilizationStatuses.length ? undefined : ids);

    const range = useMemo(() => getTimelineRange(anchor), [anchor]);
    const noStatus = !statusIds.length;
    // All options checked sends no `statuses` — the same result, and rows with any filter status show.
    const statuses = useMemo(
        () =>
            statusIds.length === authUtilizationStatuses.length
                ? []
                : authUtilizationStatuses.filter((item) => statusIds.includes(item.id)).flatMap((item) => item.values),
        [statusIds]
    );
    // One per popover filter that narrows the page. Status isn't counted — its chips show their own state.
    const activeFiltersCount = (selectedClients.length ? 1 : 0) + (clientStatusIds.length ? 1 : 0);
    const handleResetAll = (handleClose) => {
        const params = {...info};
        POPOVER_FILTER_KEYS.forEach((key) => delete params[key]);
        SaveParams(navigate, params);
        handleClose();
    };
    // Chips under the toolbar for the popover filters.
    const filtersList = [
        {name: 'Client', key: 'clients', render: (client) => renderClientName(client)},
        {name: 'Client Status', key: 'clientStatuses', options: clientStatuses},
    ];
    const {entries, applied, loading, reloading, hasMore, failed, loadMore, retry} = useUtilizationPages(
        range,
        search,
        statuses,
        clientIds,
        clientStatusIds,
        !noStatus
    );
    const clients = useMemo(
        () => (noStatus ? [] : buildUtilizationClients(entries, applied.range, applied.statuses)),
        [entries, applied, noStatus]
    );

    const sentinel = useRef(null);
    const loadMoreRef = useRef(loadMore);
    loadMoreRef.current = loadMore;

    useEffect(() => {
        if (!sentinel.current) return undefined;
        const observer = new IntersectionObserver((items) => items[0].isIntersecting && loadMoreRef.current());
        observer.observe(sentinel.current);
        return () => observer.disconnect();
    }, [clients.length, hasMore, loading, failed]);

    return (
        <div className="auth-util-page">
            <div className="auth-util-toolbar">
                <div className="auth-util-filters">
                    <FilterPopover activeFiltersCount={activeFiltersCount} onResetAll={handleResetAll}>
                        <ClientFilter value={selectedClients} onChange={setSelectedClients}/>
                        <ClientStatusFilter value={clientStatusIds} onChange={setClientStatusIds}/>
                    </FilterPopover>
                    <MonthNavigation
                        anchor={anchor}
                        onPrev={() => setAnchor((prev) => prev.clone().subtract(1, 'month'))}
                        onNext={() => setAnchor((prev) => prev.clone().add(1, 'month'))}
                        // Keep the day of month (like the arrows), so picking the current month returns to today's view.
                        onSelect={(date) => setAnchor((prev) => prev.clone().year(date.getFullYear()).month(date.getMonth()))}
                    />
                </div>
                <StatusChips value={statusIds} onChange={setStatusIds}/>
            </div>
            <SelectedFilters filtersList={filtersList} resetParams={{}} marginBottom={0} doNotCrop/>

            {/* The timeline header stays up through loading, errors and empty results. */}
            <div className={`auth-util-scroll${reloading && clients.length ? ' auth-util-reloading' : ''}`}
                 aria-busy={reloading}>
                {reloading && !!clients.length && (
                    <div className="auth-util-reloading-indicator">
                        <MinLoader margin="120px 0 0" color="#347AF0" position="relative" width="32px" height="32px"/>
                    </div>
                )}
                <UtilizationTimeline
                    clients={clients}
                    range={clients.length ? applied.range : range}>
                    {clients.length ? null : reloading && !noStatus ? (
                            <TimelineSkeleton/>
                        ) :
                        // : failed && !noStatus ? (
                        // <LoadFailed text="Couldn't load authorization utilization." onRetry={retry} />
                        // ) :
                        (
                            <NoItemsYet
                                text="No authorizations match the selected filters"
                                containerStyles={{position: 'relative', height: '55vh'}}
                            />
                        )}
                </UtilizationTimeline>
                {!!clients.length && hasMore && !reloading && !failed && (
                    <div ref={sentinel} className="auth-util-more">
                        {loading && <MinLoader margin="0" color="#347AF0" position="relative"/>}
                    </div>
                )}
                {!!clients.length && hasMore && failed && !loading &&
                    <LoadFailed text="Couldn't load more clients." onRetry={retry}/>}
            </div>
        </div>
    );
};
